import { debugLog } from 'shared/logger/debugLog';
import { DocumentModel } from 'shared/domain/document/documentModel';
import { SyncStatus } from 'shared/domain/entitySyncStatus/syncStatus';
import {
  __HttpRequestModel__,
  HttpRequestModelType,
} from 'shared/domain/httpRequest/httpRequestModel';
import { swSignals } from 'serviceWorker/events/swSignals';
import { isUploadOf } from './abortedUpload';
import { broadcastUpdatedDocument } from 'serviceWorker/broadcasts/documents';
import { buildDocumentMergePayload } from 'serviceWorker/repository/document/mergeDocumentFromApiResponse';
import { ISendDocumentUrlBuilder } from 'serviceWorker/repository/document/sendDocumentUrlBuilder';
import { UploadClearDrawing } from 'serviceWorker/repository/document/uploadClearDrawing';
import {
  IDocumentUploader,
  UploadDocument,
} from 'serviceWorker/repository/document/uploadDocument';
import { UploadDocumentChange } from 'serviceWorker/repository/document/uploadDocumentChange';
import { UploadDocumentDelete } from 'serviceWorker/repository/document/uploadDocumentDelete';
import { UploadDrawing } from 'serviceWorker/repository/document/uploadDrawing';
import { Services } from 'shared/domain/messages/message';
import type { DocumentChanges } from 'serviceWorker/db/documents';
import type { IHttpRequestRunner } from 'shared/domain/httpRequest/httpRequestRunner';

export class DocumentRequestRunner implements IHttpRequestRunner {
  private abortController: AbortController | undefined;
  private request: __HttpRequestModel__ | undefined;

  constructor(
    private documentUploader: IDocumentUploader,
    private getDocument: (id: number) => Promise<DocumentModel>,
    private hardDelete: (id: number[]) => Promise<any>,
    private urlBuilder: ISendDocumentUrlBuilder,
    private uploadDocumentUseCase: UploadDocument,
    private uploadDocumentChangeUseCase: UploadDocumentChange,
    private uploadDocumentDeleteUseCase: UploadDocumentDelete,
    private updateDocument: (
      id: number,
      changes: DocumentChanges,
      options?: { skipModifyTime: boolean }
    ) => Promise<any>
  ) {
    swSignals.uploadAborted.subscribe((detail) => {
      if (this.request && isUploadOf(this.request, detail)) {
        this.abortController?.abort();
      }
    });
  }

  execute(request: __HttpRequestModel__): Promise<boolean> {
    const abortController = new AbortController();
    this.abortController = abortController;
    this.request = request;
    switch (request.method) {
      case 'POST': {
        return this.uploadDocument(request, abortController);
      }
      case 'PUT': {
        return this.uploadDocumentChange(request);
      }
      case 'DELETE': {
        return this.uploadDocumentDelete(request);
      }
      case 'GET': {
        throw new Error('Not implemented.');
      }
    }
  }

  private async uploadDocument(
    request: __HttpRequestModel__,
    abortController: AbortController
  ): Promise<boolean> {
    const document = await this.getDocument(request.data.localId);
    const url = await this.urlBuilder.execute(
      document,
      request.data.projectId
    );

    return this.uploadDocumentUseCase(
      abortController,
      this.documentUploader,
      document,
      url
    );
  }

  private async uploadDocumentChange(
    request: __HttpRequestModel__
  ): Promise<boolean> {
    const document = await this.getDocument(request.data.localId);
    const url = await this.urlBuilder.execute(
      document,
      request.data.projectId
    );

    const response = await this.uploadDocumentChangeUseCase(
      this.documentUploader,
      url,
      request.data.changes
    );

    if (response && response.document && typeof response.document === 'object') {
      const localId = request.data.localId;
      const apiDoc = response.document as Record<string, unknown>;
      const mergePayload = buildDocumentMergePayload(apiDoc);
      debugLog(
        '[documentRequestRunner] PUT document response → merge',
        {
          localId,
          fromApi: {
            drawingSyncStatus: apiDoc.drawingSyncStatus,
            drawings: apiDoc.drawings,
            dataIsDrawn: apiDoc.data != null && typeof apiDoc.data === 'object' ? (apiDoc.data as Record<string, unknown>).isDrawn : undefined,
            dataDrawingSrc: apiDoc.data != null && typeof apiDoc.data === 'object' ? (apiDoc.data as Record<string, unknown>).drawingSrc : undefined,
          },
          mergePayload: {
            drawingSyncStatus: mergePayload.drawingSyncStatus,
            drawings: mergePayload.drawings,
            dataIsDrawn: mergePayload.data?.isDrawn,
            dataDrawingSrc: mergePayload.data?.drawingSrc,
          },
        }
      );
      if (Object.keys(mergePayload).length > 0) {
        await this.updateDocument(localId, mergePayload as DocumentChanges, {
          skipModifyTime: Boolean(mergePayload.modifiedAt),
        });
        broadcastUpdatedDocument(localId);
      }
    }

    return response !== false;
  }

  private async uploadDocumentDelete(
    request: __HttpRequestModel__
  ): Promise<boolean> {
    const documentLocalId = request.data.localId;
    let document: DocumentModel;
    try {
      document = await this.getDocument(documentLocalId);
    } catch (error) {
      await this.hardDelete([documentLocalId]);
      return true;
    }
    const url = await this.urlBuilder.execute(
      document,
      request.data.projectId
    );

    try {
      const result = await this.uploadDocumentDeleteUseCase(
        this.documentUploader,
        url
      );
      await this.hardDelete([documentLocalId]);
      return result;
    } catch (e) {
      this.updateDocument(
        documentLocalId,
        { deleted: false, syncStatus: SyncStatus.SUCCESS },
        { skipModifyTime: true }
      );
      return false;
    }
  }
}

export class DrawingRequestRunner implements IHttpRequestRunner {
  constructor(
    private documentUploader: IDocumentUploader,
    private getDocument: (id: number) => Promise<DocumentModel>,
    private urlBuilder: ISendDocumentUrlBuilder,
    private uploadDrawingUseCase: UploadDrawing,
    private uploadClearDrawingUseCase: UploadClearDrawing
  ) {}

  async execute(request: __HttpRequestModel__): Promise<boolean> {
    const document = await this.getDocument(request.data.localId);
    const url = await this.urlBuilder.execute(
      document,
      request.data.projectId
    );

    const changes = request.data.changes ?? {};
    const requestIsDrawn = changes.isDrawn ?? changes['data.isDrawn'];
    const requestDrawingSrc = changes.drawingSrc ?? changes['data.drawingSrc'];
    const requestMergedSrc = changes.mergedSrc ?? changes['data.mergedSrc'];

    const resolvedIsDrawn =
      typeof requestIsDrawn === 'boolean'
        ? requestIsDrawn
        : Boolean(document.data?.isDrawn);
    const resolvedDrawingSrc =
      requestDrawingSrc || document.data?.drawingSrc;
    const resolvedMergedSrc = requestMergedSrc || document.data?.mergedSrc;

    if (
      !resolvedIsDrawn &&
      !resolvedDrawingSrc
    ) {
      return this.uploadClearDrawingUseCase(
        this.documentUploader,
        document,
        url
      );
    }

    return this.uploadDrawingUseCase(
      this.documentUploader,
      document,
      url,
      {
        isDrawn: resolvedIsDrawn,
        drawingSrc: resolvedDrawingSrc,
        mergedSrc: resolvedMergedSrc,
        fileType: changes.fileType ?? document.type,
        drawings: changes.drawings,
      }
    );
  }
}
